<script setup>
import { getCurrentInstance } from 'vue'
import { objectOmit } from '@vueuse/core'

const props = defineProps({
  width: {
    type: String,
    default: '480px',
  },
  ok: {
    type: Function,
    default: null,
  },
  cancel: {
    type: Function,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  hideCancel: {
    type: Boolean,
    default: false,
  },
  hideActions: {
    type: Boolean,
    default: false,
  },
  okText: {
    type: String,
    default: null,
  },
  okClass: {
    type: [String, Object],
    default: null,
  },
  cancelText: {
    type: String,
    default: null,
  },
  cancelClass: {
    type: [String, Object],
    default: null,
  },
  sensors: {
    type: String,
    default: '',
  },
  actionClass: {
    type: String,
    default: '',
  },
  divider: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  autoClosed: {
    type: Boolean,
    default: true,
  },
  customStyle: {
    type: Object,
    default: null,
  },
  headerStyle: {
    type: [String, Object],
    default: null,
  },
})
const emits = defineEmits(['update:show', 'ok', 'cancel', 'closed'])
const vm = getCurrentInstance()
function close() {
  if (props.autoClosed) {
    emits('closed')
    vm.parent.emit('update:show', false)
  }
}

async function onButton(actionName) {
  const fn = props[actionName]
  let ok
  if (fn) {
    const success = await fn()
    if (success) {
      ok = true
      emits(actionName, true)
    }
  }
  else {
    ok = true
    emits(actionName, false)
  }

  if (ok)
    close()
}

const defaultHeaderStyle = '--n-padding-left:24px;--n-padding-top:0;--n-close-border-radius:999px;--n-close-size: 24px;--n-close-color-hover:var(--fillInputHovered);'

const mergedHeaderStyle = computed(() => {
  if (!props.headerStyle)
    return defaultHeaderStyle
  if (typeof props.headerStyle === 'string') {
    return `${defaultHeaderStyle};${props.headerStyle}`
  }
  return [defaultHeaderStyle, props.headerStyle]
})
</script>

<template>
  <n-modal
    :auto-focus="false"
    preset="card"
    :style="[`width: ${width}`, customStyle]"
    :bordered="false"
    size="large"
    aria-modal="true"
    class="x-modal"
    :on-close="close"
    :header-class="`h-[72px] ${divider && 'border-b border-border'}`"
    :header-style="mergedHeaderStyle"
    content-style="--n-padding-left:24px;--n-padding-bottom:24px;"
  >
    <template v-for="name in Object.keys(objectOmit($slots, ['actions', 'ok', 'default']))" :key="name" #[name]>
      <slot :name="name" />
    </template>
    <slot />
    <slot name="actions">
      <div v-if="!hideActions" class="flex mt-4 gap-x-4" :class="actionClass">
        <CommonXButton v-if="!hideCancel" :class="cancelClass" class="flex-1" type="tertiary" @click="onButton('cancel')">
          {{ cancelText || $t('取消') }}
        </CommonXButton>
        <slot name="ok">
          <CommonXButton
            type="primary"
            class="flex-1"
            :class="okClass"
            :disabled="disabled || loading"
            @click="onButton('ok')"
          >
            {{ okText || $t('确定') }}
          </CommonXButton>
        </slot>
      </div>
    </slot>
  </n-modal>
</template>
